import styled from '@emotion/styled';

import { fontFamily, fontWeight } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import Separator from 'ms-ui-primitives/Separator';

import enhancedEngagementWhite from './assets/enhanced-engagement-white.svg';
import enhancedEngagement from './assets/enhanced-engagement.svg';
import fullCurriculumCoverageWhite from './assets/full-curriculum-coverage-white.svg';
import fullCurriculumCoverage from './assets/full-curriculum-coverage.svg';
import helpfulContextWhite from './assets/helpful-context-white.svg';
import helpfulContext from './assets/helpful-context.svg';
import improvedEveryDayWhite from './assets/improved-everyday-white.svg';
import improvedEveryDay from './assets/improved-everyday.svg';
import meetsRequirementsWhite from './assets/meets-requirements-white.svg';
import meetsRequirements from './assets/meets-requirements.svg';
import openendedTasksWhite from './assets/openended-tasks-white.svg';
import openendedTasks from './assets/openended-tasks.svg';

const FeaturesWrapper = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  fontFamily: fontFamily.body,
});

const FeatureWrapper = styled.div({
  flexBasis: '50%',
  flexGrow: 1,
  flexShrink: 0,
  minWidth: 300,
  paddingRight: 32,
  paddingTop: 16,
  paddingBottom: 16,
});

const FeatureTitle = styled.h4<{ isDarkMode?: boolean | undefined }>(
  {
    fontSize: 18,
    lineHeight: '24px',
    color: colors.cloudBurst,
    fontWeight: fontWeight.semibold,
    paddingBottom: 16,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  ({ isDarkMode }) => isDarkMode && { color: colors.almond50 },
);

const FeatureIcon = styled.img({
  height: 28,
  width: 28,
});

const FeatureDescription = styled.p<{ isDarkMode?: boolean | undefined }>(
  {
    fontWeight: fontWeight.normal,
    fontSize: 14,
    lineHeight: '18px',
    color: colors.regentGray,
  },
  ({ isDarkMode }) => isDarkMode && { color: colors.almond50 },
);

type Props = {
  isDarkMode?: boolean | undefined;
};

export function FeaturesSection({ isDarkMode }: Props) {
  return (
    <FeaturesWrapper>
      <FeatureWrapper>
        <FeatureTitle isDarkMode={isDarkMode}>
          <FeatureIcon
            src={
              isDarkMode ? fullCurriculumCoverageWhite : fullCurriculumCoverage
            }
          />
          <Separator size={2} />
          Full curriculum coverage
        </FeatureTitle>
        <FeatureDescription isDarkMode={isDarkMode}>
          All content is up-to-date with any changes made by your curriculum
          authority.
        </FeatureDescription>
      </FeatureWrapper>

      <FeatureWrapper>
        <FeatureTitle isDarkMode={isDarkMode}>
          <FeatureIcon
            src={isDarkMode ? improvedEveryDayWhite : improvedEveryDay}
          />
          <Separator size={2} />
          Improved everyday
        </FeatureTitle>
        <FeatureDescription isDarkMode={isDarkMode}>
          Our in-house content team refines and creates content every single
          day.
        </FeatureDescription>
      </FeatureWrapper>

      <FeatureWrapper>
        <FeatureTitle isDarkMode={isDarkMode}>
          <FeatureIcon
            src={isDarkMode ? helpfulContextWhite : helpfulContext}
          />
          <Separator size={2} />
          Helpful context
        </FeatureTitle>
        <FeatureDescription isDarkMode={isDarkMode}>
          Easily read through prerequisite content for your curriculum.
        </FeatureDescription>
      </FeatureWrapper>

      <FeatureWrapper>
        <FeatureTitle isDarkMode={isDarkMode}>
          <FeatureIcon
            src={isDarkMode ? enhancedEngagementWhite : enhancedEngagement}
          />
          <Separator size={2} />
          Enhanced engagement
        </FeatureTitle>
        <FeatureDescription isDarkMode={isDarkMode}>
          Scaffolded to enhance engagement for a wide variety of learners.
        </FeatureDescription>
      </FeatureWrapper>

      <FeatureWrapper>
        <FeatureTitle isDarkMode={isDarkMode}>
          <FeatureIcon
            src={isDarkMode ? meetsRequirementsWhite : meetsRequirements}
          />
          <Separator size={2} />
          Meets requirements
        </FeatureTitle>
        <FeatureDescription isDarkMode={isDarkMode}>
          Necessary skills and subtopics are included to support the curriculum
          requirements.
        </FeatureDescription>
      </FeatureWrapper>

      <FeatureWrapper>
        <FeatureTitle isDarkMode={isDarkMode}>
          <FeatureIcon
            src={isDarkMode ? openendedTasksWhite : openendedTasks}
          />
          <Separator size={2} />
          Open-ended tasks
        </FeatureTitle>
        <FeatureDescription isDarkMode={isDarkMode}>
          Investigative content that encourages student discussions and connects
          concepts.
        </FeatureDescription>
      </FeatureWrapper>
    </FeaturesWrapper>
  );
}
